// Friend-system client. Same failure contract as asyncClient — network
// or non-2xx errors return null (or a tagged-error variant where the
// caller needs to distinguish error kinds, e.g. "already friends" vs
// "unknown player"). Auth, base URL, and token handling come from
// asyncClient via the shared authedFetch helper.

import { authedFetch } from './asyncClient';

export interface FriendSummary {
  id: string;
  handle: string | null;
  lastSeenAt: string | null;
  rating: number;
}

export interface FriendsList {
  mutual: FriendSummary[];
  incoming: FriendSummary[];
  outgoing: FriendSummary[];
}

export interface PlayerSearchResult {
  id: string;
  handle: string | null;
  lastSeenAt: string | null;
}

/** GET /friends */
export async function listFriends(): Promise<FriendsList | null> {
  const res = await authedFetch('/friends');
  if (!res || !res.ok) return null;
  try {
    return (await res.json()) as FriendsList;
  } catch {
    return null;
  }
}

/** GET /players/search?q=... — empty q returns empty results (no fetch). */
export async function searchPlayers(q: string): Promise<PlayerSearchResult[]> {
  const trimmed = q.trim();
  if (trimmed.length < 2) return [];
  const res = await authedFetch(`/players/search?q=${encodeURIComponent(trimmed)}`);
  if (!res || !res.ok) return [];
  try {
    const json = await res.json();
    return Array.isArray(json?.results) ? (json.results as PlayerSearchResult[]) : [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Mutations — Result-shaped returns so the UI can show specific feedback.
// ---------------------------------------------------------------------------

export type FriendRequestResult =
  | { ok: true }
  | { ok: false; code: 'already_exists' | 'unknown_player' | 'network' };

export async function requestFriend(friendId: string): Promise<FriendRequestResult> {
  const res = await authedFetch('/friends/request', {
    method: 'POST',
    body: JSON.stringify({ friendId }),
  });
  if (!res) return { ok: false as const, code: 'network' };
  if (res.ok) return { ok: true as const };
  if (res.status === 409) return { ok: false as const, code: 'already_exists' };
  if (res.status === 404) return { ok: false as const, code: 'unknown_player' };
  return { ok: false as const, code: 'network' };
}

export async function acceptFriend(requesterId: string): Promise<boolean> {
  const res = await authedFetch('/friends/accept', {
    method: 'POST',
    body: JSON.stringify({ requesterId }),
  });
  return !!res && res.ok;
}

export async function rejectFriend(requesterId: string): Promise<boolean> {
  const res = await authedFetch('/friends/reject', {
    method: 'POST',
    body: JSON.stringify({ requesterId }),
  });
  return !!res && res.ok;
}

export async function removeFriend(friendId: string): Promise<boolean> {
  const res = await authedFetch(`/friends/${encodeURIComponent(friendId)}`, {
    method: 'DELETE',
  });
  return !!res && res.ok;
}

/** POST /friends/invite → token string */
export async function createInvite(): Promise<string | null> {
  const res = await authedFetch('/friends/invite', { method: 'POST' });
  if (!res || !res.ok) return null;
  try {
    const json = await res.json();
    return typeof json?.token === 'string' ? json.token : null;
  } catch {
    return null;
  }
}

export type RedeemResult =
  | { ok: true; inviterId: string }
  | { ok: false; code: 'invalid_or_expired' | 'self_redeem' | 'network' };

export async function redeemInvite(token: string): Promise<RedeemResult> {
  const res = await authedFetch('/friends/redeem', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  if (!res) return { ok: false as const, code: 'network' };
  if (res.ok) {
    try {
      const json = await res.json();
      return { ok: true as const, inviterId: json.inviterId };
    } catch {
      return { ok: false as const, code: 'network' };
    }
  }
  if (res.status === 410) return { ok: false as const, code: 'invalid_or_expired' };
  if (res.status === 400) return { ok: false as const, code: 'self_redeem' };
  return { ok: false as const, code: 'network' };
}

/** POST /players/me — update server-side handle. Idempotent. */
export async function updateMyHandle(handle: string): Promise<boolean> {
  const res = await authedFetch('/players/me', {
    method: 'POST',
    body: JSON.stringify({ handle }),
  });
  return !!res && res.ok;
}

/**
 * Builds a shareable URL for a friend-invite token. Uses the current
 * origin + Vite BASE_URL so the link works from both /cve/ (web) and
 * the Capacitor host.
 */
export function inviteLink(token: string): string {
  // From the Android app, origin is https://localhost: a link nobody else can open.
  const base = import.meta.env.MODE === 'app'
    ? 'https://raulcontrerasherrera.com/cve/'
    : `${window.location.origin}${import.meta.env.BASE_URL}`;
  return `${base}friends?invite=${encodeURIComponent(token)}`;
}

// ---------------------------------------------------------------------------
// Report and block. Google Play's user-generated-content policy requires
// both wherever a player can see another player's handle: the ladder,
// friend search, requests and the crew list. Blocking is unilateral and
// the other side is never told; the server hides them from every list
// and from opponent selection in both directions.
// ---------------------------------------------------------------------------

export type ReportReason = 'handle' | 'harassment' | 'cheating' | 'other';

export interface BlockedPlayer {
  id: string;
  handle: string | null;
  blockedAt: string;
}

export async function reportPlayer(
  playerId: string,
  reason: ReportReason,
  details = '',
): Promise<boolean> {
  const res = await authedFetch(`/players/${encodeURIComponent(playerId)}/report`, {
    method: 'POST',
    body: JSON.stringify({ reason, details }),
  });
  return !!res && res.ok;
}

export async function blockPlayer(playerId: string): Promise<boolean> {
  const res = await authedFetch(`/players/${encodeURIComponent(playerId)}/block`, {
    method: 'POST',
  });
  return !!res && res.ok;
}

export async function unblockPlayer(playerId: string): Promise<boolean> {
  const res = await authedFetch(`/players/${encodeURIComponent(playerId)}/block`, {
    method: 'DELETE',
  });
  return !!res && res.ok;
}

/** GET /players/blocked -- null on network failure, [] when nobody is blocked. */
export async function listBlocked(): Promise<BlockedPlayer[] | null> {
  const res = await authedFetch('/players/blocked');
  if (!res || !res.ok) return null;
  try {
    const json = await res.json();
    return Array.isArray(json?.blocked) ? (json.blocked as BlockedPlayer[]) : [];
  } catch {
    return null;
  }
}
